import { ColorHelper } from '../utils/color-helper';
import { formatNumber } from '../utils/format.helper';
import { renderTooltip } from './helper';

export const DEFAULT_CHART_OPTIONS = {
  title: { text: undefined },
  colors: [
    '#1ddd58',
    '#1ddd58',
    '#FF0000',
    '#006B6B',
    '#7BA81C',
    '#B500B5',
    '#FF8787',
    '#2E96FF',
    '#FF7A00',
    '#B50000',
    '#223499',
    '#17A68C',
    '#8F8A00',
    '#8A2BE2',
    '#3477DB',
    '#D48364',
    '#800000',
    '#03A0B5',
    '#BC8A0B',
    '#483D8B',
    '#0F6C78',
    '#076DBB',
    '#B8540B',
    '#886288',
  ],
  rangeSelector: { enabled: false },
  navigator: { enabled: false },
  scrollbar: { enabled: false },
  credits: { enabled: false },
  exporting: { enabled: false },
  chart: {
    zooming: {
      type: 'x',
      mouseWheel: { enabled: false },
    },
    animation: false,
    type: 'line',
    height: 380,
    panning: { enabled: false },
    spacing: [25, 0, 10, 0],
    backgroundColor: 'transparent',
  },
  lang: {
    downloadCSV: 'Exportar para CSV',
    downloadXLS: 'Exportar para Excel',
    noData: 'Nenhum dado encontrado',
  },
  legend: {
    enabled: false,
    align: 'left',
    verticalAlign: 'top',
    y: -5,
    x: 0,
    floating: false,
    borderWidth: 0,
    maxHeight: 50,
  },
  noData: {
    style: {
      fontWeight: '400',
      fontSize: '1rem',
    },
    position: { align: 'center', verticalAlign: 'middle' },
  },
  plotOptions: {
    series: {
      connectNulls: true,
      lineWidth: 1.8,
      compare: 'percent',
      compareBase: 0,
      compareStart: true,
      events: {
        click: function () {
          this.chart.tooltip.hide();
        },
      },
      marker: { enabled: false },
    },
    pie: {
      dataLabels: { enabled: false },
      innerSize: '50%',
      allowPointSelect: true,
      cursor: 'pointer',
      startAngle: 90,
      showInLegend: true,
      animation: false,
      colors: ColorHelper.COLORS,
    },
    column: {
      borderRadius: 2,
      borderWidth: 0,
      stacking: 'normal',
      dataLabels: { enabled: false },
      colors: ColorHelper.COLORS,
    },
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 5,
        enabled: true,
      },
    },
    area: {
      fillOpacity: 0.2,
    },
  },
  yAxis: {
    title: { text: null },
    opposite: false,
    lineWidth: 0,
    tickPixelInterval: 40,
    lineColor: '#666',
    gridLineColor: '#BBB',
    gridLineDashStyle: 'Dot',
    startOnTick: true,
    endOnTick: true,
    max: null,
    labels: {
      formatter() {
        return formatNumber(this.value, {
          type: 'percent',
          hideZero: true,
          useNullPlaceholder: false,
        });
      },
    },
    plotLines: [
      {
        color: '#CCC',
        width: 1,
        value: 0,
        zIndex: 2,
      },
    ],
  },
  xAxis: {
    type: 'datetime',
    lineWidth: 1,
    lineColor: '#666',
    dateTimeLabelFormats: {
      day: { main: '%m/%y' },
      month: { main: '%m/%y' },
      week: { main: '%m/%y' },
      year: { main: '%Y' },
    },
  },
  responsive: {
    rules: [
      {
        condition: {
          maxHeight: 270,
        },
      },
    ],
  },
  tooltip: {
    shared: true,
    split: false,
    shadow: false,
    borderWidth: 1,
    useHTML: true,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderColor: '#CCC',
    formatter() {
      return renderTooltip.call(this, 'percent');
    },
  },
} as Highcharts.Options;

export const QUOTE_PROFIT_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  plotOptions: {
    series: {
      ...DEFAULT_CHART_OPTIONS.plotOptions?.series,
      compare: undefined, // this should be undefined
      compareBase: 0, // this should be 0
      compareStart: undefined, // this should be undefined
    },
  },
  yAxis: {
    ...DEFAULT_CHART_OPTIONS.yAxis,
    labels: {
      formatter() {
        return formatNumber(this.value, { type: 'money' });
      },
    },
  },
  tooltip: {
    ...DEFAULT_CHART_OPTIONS.tooltip,
    formatter() {
      return renderTooltip.call(this, 'money', 'DD/MM/YYYY', false, true);
    },
  },
} as Highcharts.Options;

export const SCATTER_CHART_OPTIONS = {
  chart: {
    ...DEFAULT_CHART_OPTIONS.chart,
    type: 'scatter',
  },
  xAxis: {
    title: { text: 'Risco' },
    labels: {
      formatter() {
        return formatNumber(this.value, { hideZero: true });
      },
    },
    startOnTick: true,
    endOnTick: true,
    showLastLabel: true,
  },
  yAxis: {
    ...DEFAULT_CHART_OPTIONS.yAxis,
    title: { text: 'Retorno anualizado (CAGR)' },
    labels: {
      formatter() {
        return formatNumber(this.value, { hideZero: true });
      },
    },
  },
  tooltip: {
    shared: true,
    split: false,
    backgroundColor: 'rgba(255,255,255,0.85)',
    borderColor: '#f3f3f3',
    borderWidth: 1,
    borderRadius: 3,
    shadow: false,
    useHTML: true,
    formatter: function () {
      let value = Number(this.x);
      return `<b style='font-weight: bold; padding-bottom: 15px;'>${
        this.series?.name
      }</b>
     <div style='padding-top: 5px; border-top: 1px solid #ccc'>Retorno: ${formatNumber(
       this?.y as any,
       { type: 'percent' }
     )},  Risco: ${formatNumber(value)}</div>`;
    },
  },
  series: [],
} as Highcharts.Options;

export const SCATTER_CHART_OPTIONS_RETURN = {
  chart: {
    ...DEFAULT_CHART_OPTIONS.chart,
    type: 'scatter',
  },
  xAxis: {
    title: { text: 'Risco' },
    labels: {
      formatter() {
        return formatNumber(this.value, { hideZero: true });
      },
    },
    startOnTick: true,
    endOnTick: true,
    showLastLabel: true,
  },
  plotOptions: { series: { marker: { radius: 4, symbol: 'circle' } } },
  yAxis: {
    title: { text: 'Retorno' },
    labels: {
      formatter() {
        return formatNumber(this.value, { hideZero: true });
      },
    },
  },
  tooltip: {
    shared: true,
    split: false,
    backgroundColor: 'rgba(255,255,255,0.85)',
    borderColor: '#f3f3f3',
    borderWidth: 1,
    borderRadius: 3,
    shadow: false,
    useHTML: true,
    formatter: function () {
      let value = Number(this.x);
      return `<b style='font-weight: bold; padding-bottom: 15px;'>${
        this.series?.name
      }</b>
     <div style='padding-top: 5px; border-top: 1px solid #ccc'>Retorno: ${formatNumber(
       this?.y as any,
       { type: 'percent' }
     )},  Risco: ${formatNumber(value)}</div>`;
    },
  },
  series: [],
} as Highcharts.Options;

export const DRAWDOWN_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  legend: { enabled: false },
  plotOptions: {
    series: {
      ...DEFAULT_CHART_OPTIONS.plotOptions?.series,
      lineWidth: 1.4,
      compare: undefined,
      compareStart: undefined,
    },
  },
} as Highcharts.Options;

export const CORRELATION_CHART_OPTIONS = {
  title: { text: '' },
  chart: {
    type: 'heatmap',
    backgroundColor: '#FFFFFF',
    marginTop: 30,
    marginRight: 30,
    marginBottom: 30,
    marginLeft: 30,
    spacing: [0, 0, 0, 0],
    style: {
      fontFamily: 'Arial',
    },
  },
  credits: { enabled: false },
  legend: { enabled: false },
  colorAxis: {
    max: 1,
    min: -1,
    stops: [
      [-1, '#E63422'],
      [-0.5, '#FF9B9B'],
      [-0.2, '#FFE5E5'],
      [0, '#FFFFFF'],
      [0.2, '#E5EDFF'],
      [0.5, '#9BB5FF'],
      [1, '#0073DE'],
    ],
  },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderWidth: 0,
    borderRadius: 4,
    shadow: true,
    useHTML: true,
    headerFormat: '',
    pointFormat: `
      <div style="text-align: center;">
        <div style="font-size: 18px; font-weight: bold; color: #212529;">{point.value:.2f}</div>
        <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 8px;">
          <div style="display: flex; align-items: center; gap: 4px;">
            <div style="width: 3px; height: 14px; background-color: {point.x_color};"></div>
            <span style="color: #666; font-size: 11px">{point.x}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 4px;">
            <div style="width: 3px; height: 14px; background-color: {point.y_color};"></div>
            <span style="color: #666; font-size: 11px">{point.y}</span>
          </div>
        </div>
      </div>
    `,
    style: {
      fontSize: '12px',
    },
  },
  yAxis: {
    reversed: true,
    title: { text: null },
    gridLineWidth: 0,
    lineWidth: 0,
    labels: {
      enabled: false,
    },
  },
  xAxis: {
    opposite: true,
    lineWidth: 0,
    gridLineWidth: 0,
    labels: {
      enabled: false,
    },
  },
  plotOptions: {
    heatmap: {
      borderWidth: 2,
      borderColor: '#FFFFFF',
      states: {
        hover: {
          brightness: 0.1,
        },
      },
      dataLabels: {
        enabled: true,
        color: '#000000',
        style: {
          textOutline: 'none',
          fontWeight: '400',
          fontSize: '12px',
          fontFamily: 'Arial',
          textAlign: 'center',
        },
        formatter: function () {
          const point = this.point as any;
          const value = point?.value;

          if (value != null && point.x !== point.y) {
            // Mudar a cor do texto baseado no valor para melhor contraste
            this.color = Math.abs(value) > 0.6 ? '#FFFFFF' : '#000000';
            return value.toFixed(2);
          }
          return '';
        },
      },
    },
  },
} as Highcharts.Options;

export const PORTFOLIO_TRANSACTIONS_CHART_OPTIONS = {
  chart: { type: 'column' },
  legend: {
    align: 'left',
    verticalAlign: 'top',
    enabled: true,
    symbolHeight: 10, // Set the height of the legend icon
    symbolWidth: 30,
    symbolRadius: 2,
    squareSymbol: false,
    itemMarginBottom: 10,
  },
  yAxis: {
    ...DEFAULT_CHART_OPTIONS.yAxis,
    labels: {
      formatter() {
        return formatNumber(this.value, {
          hideZero: true,
          thousandMask: true,
          type: 'money',
        });
      },
    },
  },
  xAxis: { type: 'category' },
  plotOptions: {
    column: {
      borderRadius: 4,
      stacking: 'normal',
    },
  },
  tooltip: {
    ...DEFAULT_CHART_OPTIONS.tooltip,
    formatter() {
      return renderTooltip.call(this, 'money', undefined, true);
    },
  },
} as Highcharts.Options;

export const PORTFOLIO_NETWORTH_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  legend: {
    align: 'left',
    verticalAlign: 'top',
    enabled: true,
    symbolHeight: 10, // Set the height of the legend icon
    symbolWidth: 30,
    symbolRadius: 2,
    squareSymbol: false,
    itemMarginBottom: 10,
  },
  yAxis: {
    ...DEFAULT_CHART_OPTIONS.yAxis,
    labels: {
      formatter() {
        return formatNumber(this.value, {
          type: 'money',
          thousandMask: true,
          hideZero: true,
        });
      },
    },
  },
  tooltip: {
    ...DEFAULT_CHART_OPTIONS.tooltip,
    formatter() {
      return renderTooltip.call(this, 'currency');
    },
  },
} as Highcharts.Options;

export const PORTFOLIO_COMPOSITION_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  chart: {
    backgroundColor: 'transparent',
    plotShadow: false,
    type: 'pie',
    animation: false,
    height: 255,
    spacingRight: 0, // Reduce spacing on the right to maximize chart area
  },
  legend: {
    enabled: true,
    itemStyle: { fontSize: '10px' },
    align: 'right',
    layout: 'horizontal',
    verticalAlign: 'middle',
    symbolHeight: 10,
    symbolWidth: 10,
    symbolRadius: 2,
    itemMarginTop: 0,
    itemMarginBottom: 3,
    margin: 0,
    itemDistance: 8,
    itemWidth: 130, // each item width
    width: 400, // available legend width; items will wrap creating 2 columns
    maxHeight: undefined, // let legend grow
    navigation: {
      arrowSize: 10,
      activeColor: '#14AE5C',
      enabled: true,
    },
  },
  tooltip: {
    pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
  },
  accessibility: {
    point: {
      valueSuffix: '%',
    },
  },
} as Highcharts.Options;

export const PORTFOLIO_PROFITABILITY_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  legend: {
    align: 'left',
    verticalAlign: 'top',
    enabled: true,
    symbolHeight: 8, // Set the height of the legend icon
    itemMarginBottom: 8,
    symbolWidth: 30,
    symbolRadius: 2,
    squareSymbol: false,
    maxHeight: 200,
  },
  yAxis: {
    ...DEFAULT_CHART_OPTIONS.yAxis,
    labels: {
      formatter() {
        return formatNumber(this.value, { type: 'percent' });
      },
    },
  },
  xAxis: {
    type: 'datetime',
  },
  tooltip: {
    ...DEFAULT_CHART_OPTIONS.tooltip,
    formatter() {
      return renderTooltip.call(this, 'currency');
    },
  },
} as Highcharts.Options;

export const PORTFOLIO_NETWORTH_ALLOCATION = {
  chart: { type: 'treemap' },
  colorAxis: {
    maxColor: '#14AE5C',
    minColor: '#a3e4c1',
  },
  plotOptions: {
    treemap: {
      dataLabels: {
        enabled: true,
        shadow: false,
        style: {
          color: '#fff',
          textOutline: 'none',
        },
        formatter: function () {
          return `
            <p style="font-size:0.85rem">
            ${this.point.name} <br />${formatNumber(this.point.value!, {
            type: 'money',
          })}
            `;
        },
      },
    },
  },
  tooltip: {
    enabled: true,
    shared: true,
    split: false,
    shadow: false,
    borderWidth: 1,
    useHTML: true,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255)',
    borderColor: '#CCC',
    formatter: function () {
      return `
        <p style="font-size:0.85rem">
        ${this.point.name} <br />${formatNumber(this.point.value!, {
        type: 'money',
      })}
        `;
    },
  },
} as Highcharts.Options;

export const PORTFOLIO_INCOMES_BY_ASSET_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  chart: {
    backgroundColor: 'transparent',
    plotShadow: false,
    type: 'pie',
    animation: false,
    height: 300,
  },
  credits: { enabled: false },
  legend: { enabled: false },
  tooltip: {
    pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
  },
  accessibility: {
    point: {
      valueSuffix: '%',
    },
  },
  plotOptions: {
    pie: {
      innerSize: '50%',
      allowPointSelect: true,
      cursor: 'pointer',
      dataLabels: {
        enabled: false,
      },
      startAngle: 90,
      showInLegend: true,
      animation: false,
    },
  },
} as Highcharts.Options;

export const PORTFOLIO_INCOMES_HISTORY_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  chart: { type: 'column', height: 340 },
  legend: {
    align: 'left',
    verticalAlign: 'top',
    enabled: true,
    symbolHeight: 10, // Set the height of the legend icon
    symbolWidth: 30,
    symbolRadius: 2,
    squareSymbol: false,
    itemMarginBottom: 10,
    maxHeight: 100,
  },
  xAxis: { type: 'category' },
  yAxis: {
    ...DEFAULT_CHART_OPTIONS.yAxis,
    labels: {
      formatter() {
        return formatNumber(this.value, {
          hideZero: true,
          thousandMask: true,
          type: 'money',
        });
      },
    },
  },
  tooltip: {
    ...DEFAULT_CHART_OPTIONS.tooltip,
    formatter() {
      return renderTooltip.call(this, 'money', undefined, true);
    },
  },
} as Highcharts.Options;

export const FUND_WALLET_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  chart: {
    backgroundColor: 'transparent',
    plotShadow: false,
    type: 'pie',
    animation: false,
    height: 260,
  },
  credits: { enabled: false },
  legend: {
    enabled: true,
    itemStyle: { fontSize: '11px' },
    align: 'right',
    layout: 'vertical',
    verticalAlign: 'top',
    y: 60,
    symbolHeight: 12,
    symbolWidth: 24,
    symbolRadius: 2,
    width: 200,
  },
  tooltip: {
    formatter() {
      return `
        <span style="font-size: 14px; font-weight: 600;">${
          this.point.name
        }</span></br></br>
        <b>Valor:</b>  ${formatNumber(this.point.y!, {
          type: 'money',
          thousandMask: true,
        })}
        </br>
        <b>% carteira:</b> ${formatNumber(this.point.percentage!, {
          type: 'percent',
        })}
      `;
    },
  },
  accessibility: {
    point: {
      valueSuffix: '%',
    },
  },
} as Highcharts.Options;

export const SUCCESS_RATE_CHART_OPTIONS = {
  title: { text: undefined },
  rangeSelector: { enabled: false },
  navigator: { enabled: false },
  scrollbar: { enabled: false },
  credits: { enabled: false },
  exporting: { enabled: false },
  chart: {
    type: 'column',
    height: 220,
    zooming: {
      type: 'x',
      mouseWheel: {
        enabled: false,
      },
    },
    panning: { enabled: false },
    backgroundColor: 'transparent',
  },
  lang: { noData: 'Nenhum dado encontrado' },
  legend: {
    enabled: false,
    align: 'left',
    verticalAlign: 'top',
    y: -5,
    x: 0,
    floating: false,
    borderWidth: 0,
  },
  noData: {
    style: {
      fontWeight: '400',
      fontSize: '1rem',
    },
    position: { align: 'center', verticalAlign: 'middle' },
  },
  plotOptions: {
    series: {
      connectNulls: true,
      lineWidth: 2,
      compare: undefined,
      compareBase: 0,
      compareStart: true,
      events: {
        click: function () {
          this.chart.tooltip.hide();
        },
      },
    },
    column: {
      pointPadding: 0.2,
      borderWidth: 0,
    },
  },
  yAxis: {
    title: { text: null },
    opposite: false,
    lineWidth: 0,
    lineColor: '#666',
    gridLineDashStyle: 'Dot',
    gridLineColor: '#BBB',
    plotLines: [
      {
        color: '#CCC',
        width: 1,
        value: 0,
        zIndex: 2,
      },
    ],
    startOnTick: true,
    endOnTick: true,
    min: 0,
    max: 100,
    tickPixelInterval: 35,
    minorTickLength: 0.0001,
    minorTicks: false,
    minPadding: 0.0001,
    maxPadding: 0.0001,
    labels: {
      formatter() {
        return formatNumber(this.value, { type: 'percent', hideZero: true });
      },
    },
  },
  xAxis: {
    categories: [
      '3 meses',
      '6 meses',
      '12 meses',
      '24 meses',
      '36 meses',
      '48 meses',
      '60 meses',
    ],
    lineWidth: 1,
    lineColor: '#BBB',
    crosshair: true,
  },
  responsive: {
    rules: [
      {
        condition: {
          maxHeight: 270,
        },
      },
    ],
  },
  tooltip: {
    shared: true,
    split: false,
    shadow: false,
    borderWidth: 1,
    useHTML: true,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderColor: '#CCC',
    formatter() {
      return renderTooltip.call(this, 'percent', 'DD/MM/YYYY', true);
    },
  },
} as Highcharts.Options;

export const COMPARISON_PROFIT_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  legend: {
    ...DEFAULT_CHART_OPTIONS.legend,
    enabled: true,
  },
} as Highcharts.Options;

export const MOVABLE_WINDOWS_CHART_OPTIONS = {
  ...DEFAULT_CHART_OPTIONS,
  legend: {
    ...DEFAULT_CHART_OPTIONS.legend,
    enabled: true,
  },
  plotOptions: {
    series: {
      ...DEFAULT_CHART_OPTIONS.plotOptions?.series,
      compare: undefined,
      compareBase: 0,
      compareStart: undefined,
    },
  },
} as Highcharts.Options;
